import { hideNode, hideOptionNode, showNode, showOptionNode } from './configurator/helpers';
import { isImperialInputEmpty } from './utils/imperial-validator';

export class Questionnaire {
  constructor(options) {
    ({ onChange: this.onChange } = options);
    this.questionaire = document.querySelector('.questionnaire');
    this.questions = [];
    this.activeQuestions = [];
  }

  init() {
    this._initQuestions();
    if (this.validate()) {
      this.onChange({ valid: true });
    }
  }

  validate() {
    return (
      this.activeQuestions.every(q => {
        return q.valid;
      }) && this.lastActiveQuestion.isFinalized()
    );
  }

  questionAnswered(question, answer) {
    if (answer.valid) {
      this._deactivateAllQuestionsAfter(question);
      if (!answer.isFinal) {
        this._activateNextQuestion(answer);
      }
      this._redrawQuestions();
    } else {
      this._deactivateAllQuestionsAfter(question);
      this._redrawQuestions();
    }
    this.onChange({ valid: this.validate() });
  }

  _deactivateAllQuestionsAfter(question) {
    const index = this.activeQuestions.indexOf(question);
    this.activeQuestions = [
      ...this.activeQuestions.filter((q, i) => {
        return i <= index;
      }),
    ];
  }

  _initQuestions() {
    const questions = this.questionaire.querySelectorAll('[data-question]');
    questions.forEach(question => this._initQuestion(question));
    while (this._traverseActiveQuestions(this.lastActiveQuestion)) { }
    this._redrawQuestions();
  }

  _initQuestion(questionNode) {
    const question = new Question({
      node: questionNode,
      onChange: this.questionAnswered.bind(this),
    });
    this.questions.push(question);
    if (question.isFirst()) {
      this.activeQuestions.push(question);
    }
  }

  _activateNextQuestion(answer) {
    const nextQuestion = this.questions.find(q => q.identifier === answer?.nextQuestionIdentifier);
    if (!nextQuestion) {
      return;
    }
    nextQuestion.updateAnswersVisibility(answer.excludedAnswersIdentifiers);
    nextQuestion.open();
    this.activeQuestions.push(nextQuestion);
    if (nextQuestion.valid) {
      this._activateNextQuestion(nextQuestion.answer);
    }
  }

  _getFollowUpQuestion(question) {
    return this.questions.find(q => q.identifier === question.answer?.nextQuestionIdentifier);
  }

  _traverseActiveQuestions(quesiton) {
    const nextQuestion = this._getFollowUpQuestion(quesiton);
    if (nextQuestion) {
      this.activeQuestions.push(nextQuestion);
      return nextQuestion;
    }

    return false;
  }

  _redrawQuestions() {
    this.questions.forEach(q => {
      if (!this.activeQuestions.includes(q)) {
        q.hide();
      }
    });
  }

  get lastActiveQuestion() {
    return this.activeQuestions[this.activeQuestions.length - 1];
  }
}

export class Question {
  answers = [];

  constructor(options) {
    ({ node: this.node, onChange: this.onChange } = options);
    this.name = this.node.dataset.questionName;
    this.identifier = this.node.dataset.question;
    this.node.querySelectorAll('[data-answer]').forEach(answerNode =>
      this.answers.push(
        new Answer({
          node: answerNode,
          name: this.name,
          onChange: this.answerChanged.bind(this),
        })
      )
    );
    this.node.addEventListener('transitionend', e => {
      if (e.propertyName !== 'height') return;
      const height = parseInt(this.node.style.height);
      if (height > 0) {
        this.node.style.height = 'auto';
      }
    });
    this.updateFilteredBy();
    if (this.isAnswered()) {
      this.show();
    }
  }

  isAnswered() {
    return !!this.answers.find(answer => answer.selected);
  }

  isFirst() {
    return this.node.dataset.hasOwnProperty('questionFirst');
  }

  isFinalized() {
    return this.answer.isFinal;
  }

  answerChanged(answer) {
    this.updateFilteredBy();
    this.onChange(this, answer);
  }

  open() {
    const height = this.node.scrollHeight;
    if (this.node.style.height === 'auto') return;
    requestAnimationFrame(() => {
      this.node.style.transitionDuration = '0.5s';
      requestAnimationFrame(() => {
        this.node.style.height = ''.concat(height, 'px');
      });
    });
  }

  updateAnswersVisibility(excludedIdentifiers) {
    const toBeExcluded = excludedIdentifiers ? excludedIdentifiers.split(',') : [];
    this.answers.forEach(answer => {
      if (toBeExcluded.includes(answer.value)) {
        answer.hide();
      } else {
        answer.show();
      }
    });
  }

  show() {
    this.node.style.height = 'auto';
  }

  hide() {
    requestAnimationFrame(() => {
      this.node.style.transitionDuration = '0s';
      requestAnimationFrame(() => {
        this.node.style.height = '0';
      });
    });
  }

  updateFilteredBy() {
    const elements = document.querySelectorAll(`[data-filtered-by-question="${this.name}"]`);

    elements.forEach(el => {
      if (this.answer && this.answer.answerName && el.dataset.filteredForAnswer === this.answer.answerName) {
        showOptionNode(el);
      } else {
        hideOptionNode(el);
      }
    });
  }

  get answer() {
    return this.answers.find(a => a.answered);
  }

  get valid() {
    return !!this.answer && this.answer.valid;
  }
}

export class Answer {
  constructor(options) {
    ({ node: this.node, name: this.name, onChange: this.onChange } = options);
    this.node.addEventListener('change', e => {
      if (this.node.dataset.validate) {
        const additionalElement = document.getElementById(this.node.dataset.validate);
        additionalElement.focus();
      }
      this.onChange(this);
    });
    if (this.node.dataset.validate) {
      const additionalElement = document.getElementById(this.node.dataset.validate);

      if (additionalElement) {
        if (additionalElement.tagName.toLowerCase() === 'select') {
          additionalElement.addEventListener('change', e => {
            this.onChange(this);
          });
        } else if (additionalElement.classList.contains('imperial-input-group')) {
          additionalElement.querySelectorAll('select').forEach(select => {
            select.addEventListener('change', e => {
              this.onChange(this);
            });
          });
        } else if (
          additionalElement.tagName.toLocaleLowerCase() === 'input' &&
          (additionalElement.type.toLocaleLowerCase() === 'text' ||
            additionalElement.type.toLocaleLowerCase() === 'number')
        ) {
          additionalElement.addEventListener('input', e => {
            this.onChange(this);
          });
        }
      }
    }
  }

  get answered() {
    return this.selected;
  }

  get selected() {
    return !!this.node.checked;
  }

  get valid() {
    let isValid = this.selected;
    if (this.node.dataset.validate) {
      const inputElement = document.getElementById(this.node.dataset.validate);
      isValid = isValid && (!!inputElement.value || !isImperialInputEmpty(inputElement));
    }
    return isValid;
  }

  get nextQuestionIdentifier() {
    return this.node.dataset.nextQuestion;
  }

  get isFinal() {
    return this.node.dataset.hasOwnProperty('final');
  }

  get excludedAnswersIdentifiers() {
    const toBeExcluded = this.node.dataset.excludedAnswers;
    if (!toBeExcluded) {
      return;
    }
    return toBeExcluded;
  }

  get value() {
    return this.node.value;
  }

  get answerName() {
    return this.node.dataset.answer;
  }

  hide() {
    this.node.checked = false;
    hideNode(this.node.parentNode);
    document.querySelectorAll(`[data-answer-info-for="${this.answerName}"]`).forEach(info => hideNode(info));
  }

  show() {
    showNode(this.node.parentNode, 'flex');
    document.querySelectorAll(`[data-answer-info-for="${this.answerName}"]`).forEach(info => showNode(info));
  }
}
